import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart, formatBRL } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useAppConfig } from "@/context/AppConfigContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShoppingBag, Minus, Plus, Trash2, ShieldCheck, ArrowLeft, Sparkles, Heart } from "lucide-react";
import ShopComingSoon from "@/components/ShopComingSoon";

export default function Cart() {
  const { items, updateQty, removeItem, totalQty, subtotalCents } = useCart();
  const { user } = useAuth();
  const { shopEnabled, loading: cfgLoading } = useAppConfig();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "" });
  const [checking, setChecking] = useState(false);

  // Prefill buyer if user is logged in
  useEffect(() => {
    if (user) {
      setBuyer((b) => ({
        name: b.name || user.name || "",
        email: b.email || user.email || "",
        phone: b.phone || user.phone || "",
      }));
    }
  }, [user]);

  // Fetch preview (shipping + total) whenever items change
  useEffect(() => {
    if (!shopEnabled) return;
    if (items.length === 0) {
      setPreview(null);
      return;
    }
    const payload = { items: items.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })) };
    api.post("/shop/cart/preview", payload)
      .then(({ data }) => setPreview(data))
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Não foi possível calcular o frete.";
        toast.error(msg);
        setPreview(null);
      });
  }, [items, shopEnabled]);

  if (cfgLoading) return null;
  if (!shopEnabled) return <ShopComingSoon />;

  const goToCheckout = async () => {
    if (!buyer.email || !buyer.name) {
      toast.error("Informe seu nome e e-mail para o pagamento.");
      return;
    }
    setChecking(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
        email: buyer.email,
        name: buyer.name,
        phone: buyer.phone || null,
        origin: window.location.origin,
      };
      const { data } = await api.post("/shop/checkout", payload);
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Não foi possível iniciar o pagamento.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar pagamento.");
    } finally {
      setChecking(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container-cuidar py-16 sm:py-24 text-center">
        <div className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-[hsl(var(--muted))]">
          <ShoppingBag className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />
        </div>
        <h1 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold">Seu carrinho está vazio</h1>
        <p className="mt-3 text-[hsl(var(--muted-foreground))]">Que tal escolher uma peça e apoiar o instituto?</p>
        <Button asChild size="lg" className="mt-6 btn-pill h-12 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="cart-empty-shop-btn">
          <Link to="/produtos">Ver produtos</Link>
        </Button>
      </div>
    );
  }

  const shipping = preview?.shipping_cents ?? 0;
  const total = preview?.total_cents ?? subtotalCents;

  return (
    <div className="container-cuidar py-10 sm:py-14">
      <Link to="/produtos" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="cart-back-link">
        <ArrowLeft className="h-4 w-4" /> Continuar comprando
      </Link>
      <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold">
        Seu carrinho <span className="text-[hsl(var(--muted-foreground))] font-normal text-lg">({totalQty} {totalQty === 1 ? "item" : "itens"})</span>
      </h1>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px]">
        {/* Items */}
        <div className="space-y-3" data-testid="cart-items">
          {items.map((i) => (
            <div key={`${i.product_id}-${i.size}`} className="flex gap-4 rounded-2xl border border-[hsl(var(--border))] bg-white p-4" data-testid={`cart-item-${i.product_id}-${i.size}`}>
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-[hsl(var(--muted))] shrink-0">
                {i.image ? <img src={i.image} alt={i.name} className="w-full h-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold line-clamp-1">{i.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Tamanho: <span className="font-semibold">{i.size}</span></div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="inline-flex items-center rounded-full border border-[hsl(var(--border))] overflow-hidden">
                    <button className="p-1.5 hover:bg-[hsl(var(--muted))]" onClick={() => updateQty(i.product_id, i.size, i.qty - 1)} data-testid={`cart-dec-${i.product_id}-${i.size}`}>
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold">{i.qty}</span>
                    <button className="p-1.5 hover:bg-[hsl(var(--muted))]" onClick={() => updateQty(i.product_id, i.size, i.qty + 1)} data-testid={`cart-inc-${i.product_id}-${i.size}`}>
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(i.product_id, i.size)}
                    className="text-xs text-[hsl(var(--destructive))] hover:underline inline-flex items-center gap-1"
                    data-testid={`cart-remove-${i.product_id}-${i.size}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remover
                  </button>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading font-extrabold text-[hsl(var(--primary))]">
                  {formatBRL(i.unit_price_cents * i.qty)}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{formatBRL(i.unit_price_cents)} un.</div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-max rounded-3xl border border-[hsl(var(--border))] bg-white p-6 space-y-4" data-testid="cart-summary">
          <h2 className="font-heading text-xl font-extrabold">Resumo</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[hsl(var(--muted-foreground))]">Subtotal</span>
              <span className="font-semibold" data-testid="cart-subtotal">{formatBRL(subtotalCents)}</span>
            </div>
            {preview && preview.discount_cents > 0 && (
              <div className="flex justify-between text-[hsl(var(--primary))]" data-testid="cart-discount-line">
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <Sparkles className="h-3.5 w-3.5" />
                  Cupom {preview.discount_code} (−{preview.discount_percent_off}%)
                </span>
                <span className="font-semibold">−{formatBRL(preview.discount_cents)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[hsl(var(--muted-foreground))]">Frete</span>
              <span className="font-semibold" data-testid="cart-shipping">
                {preview ? (shipping === 0 ? "Grátis" : formatBRL(shipping)) : "Calculando…"}
              </span>
            </div>
            <div className="border-t border-[hsl(var(--border))] pt-3 flex justify-between items-baseline">
              <span className="font-heading font-bold">Total</span>
              <span className="font-heading text-2xl font-extrabold text-[hsl(var(--primary))]" data-testid="cart-total">
                {formatBRL(total)}
              </span>
            </div>
          </div>

          {preview && preview.is_donor && (
            <div className="rounded-2xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 p-3.5 flex items-start gap-2.5" data-testid="cart-donor-badge">
              <div className="mt-0.5 grid place-items-center h-8 w-8 rounded-full bg-[hsl(var(--primary))] text-white shrink-0">
                <Heart className="h-4 w-4" />
              </div>
              <div className="text-xs text-[hsl(var(--foreground))]">
                <div className="font-bold text-[hsl(var(--primary))]">Você é um doador ativo 💚</div>
                <div className="text-[hsl(var(--muted-foreground))] mt-0.5">Aplicamos automaticamente <strong>{preview.discount_percent_off}% de desconto</strong> em toda a sua compra. Obrigado pelo apoio contínuo!</div>
              </div>
            </div>
          )}

          <div className="border-t border-[hsl(var(--border))] pt-4 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Seus dados</div>
            <div>
              <Label>Nome completo</Label>
              <Input className="mt-1 rounded-xl h-11" value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} data-testid="cart-buyer-name" />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input type="email" className="mt-1 rounded-xl h-11" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} data-testid="cart-buyer-email" />
            </div>
            <div>
              <Label>Telefone (opcional)</Label>
              <Input className="mt-1 rounded-xl h-11" value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} data-testid="cart-buyer-phone" />
            </div>
          </div>

          <Button
            size="lg"
            onClick={goToCheckout}
            disabled={checking || !preview}
            data-testid="cart-checkout-btn"
            className="w-full btn-pill h-12 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
          >
            {checking ? "Redirecionando…" : "Ir para pagamento"}
          </Button>
          <div className="text-xs text-center text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Pagamento seguro via Stripe
          </div>
        </aside>
      </div>
    </div>
  );
}
